import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { PseoPage } from "@/components/laneguide/PseoPage";
import { formatKr, lendersForAmount, parseAmountSlug } from "@/lib/pseo";
import { CITIES, formatPopulation, getCity } from "@/lib/pseo-cities";
import { editorialFor, tierFaq } from "@/lib/pseo-editorial";
import { MATRIX_CATEGORIES, amountsForCategory } from "@/lib/pseo-matrix";

const BASE = "https://svensklaneguide.se";
const CITY_DEFAULT_AMOUNT = 75000;

export const Route = createFileRoute("/lan/$amount")({
  loader: ({ params }) => {
    const amount = parseAmountSlug(params.amount);
    if (amount) return { kind: "amount" as const, amount, city: null };
    const city = getCity(params.amount);
    if (city) return { kind: "city" as const, amount: CITY_DEFAULT_AMOUNT, city: city.slug };
    throw notFound();
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Sidan hittades inte" }, { name: "robots", content: "noindex" }] };
    }
    const url = `${BASE}/lan/${params.amount}`;
    let title: string;
    let description: string;
    let faq: { q: string; a: string }[];
    let crumbName: string;

    if (loaderData.kind === "city") {
      const city = getCity(loaderData.city!)!;
      title = `Lån i ${city.name} – jämför långivare och räntor 2026 | Svensk Låneguide`;
      description = `Bor du i ${city.name}? Jämför lån och långivare som beviljar lån i ${city.name}, se månadskostnad och ränta – ansök kostnadsfritt online.`;
      crumbName = `Lån i ${city.name}`;
      faq = cityFaq(city.name);
    } else {
      const kr = formatKr(loaderData.amount);
      title = `Jämför lån på ${kr} – bäst ränta 2026 | Svensk Låneguide`;
      description = `Vill du låna ${kr}? Jämför långivare som beviljar ${kr}, se månadskostnad, ränta och villkor – och ansök kostnadsfritt online.`;
      crumbName = kr;
      faq = tierFaq(loaderData.amount);
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "robots", content: "index, follow" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: BASE },
              { "@type": "ListItem", position: 2, name: "Privatlån", item: `${BASE}/privatlan` },
              { "@type": "ListItem", position: 3, name: crumbName, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: AmountOrCityPage,
});

function cityFaq(name: string) {
  return [
    {
      q: `Vilka långivare kan jag låna av i ${name}?`,
      a: `Samtliga långivare i listan ovan är rikstäckande och lånar ut i hela Sverige, inklusive ${name}. Du behöver inte besöka något kontor – hela ansökan sköts digitalt med BankID.`,
    },
    {
      q: `Påverkar min bostadsort räntan?`,
      a: `Nej, orten i sig påverkar inte räntan. Däremot vägs din boendekostnad in i bankens kalkyl, så en hög hyra eller ett stort bolån i ${name} kan sänka hur mycket du blir beviljad.`,
    },
    {
      q: `Hur snabbt får jag pengarna i ${name}?`,
      a: `Utbetalningen sker till ditt bankkonto, oftast inom en till tre bankdagar oavsett var i landet du bor. Har du konto i samma bank som långivaren kan pengarna finnas där samma dag.`,
    },
    {
      q: `Kan jag söka lån i ${name} med betalningsanmärkning?`,
      a: `Ja, flera av aktörerna gör en helhetsbedömning av din ekonomi. Se vår sida om lån med betalningsanmärkning för de långivare som kan bevilja lån trots anmärkning.`,
    },
  ];
}

function AmountOrCityPage() {
  const data = Route.useLoaderData();
  const [amount, setAmount] = useState(data.amount);
  const lenders = lendersForAmount(amount);
  const city = data.city ? getCity(data.city) : null;

  if (city) {
    const related = [
      ...MATRIX_CATEGORIES.map((c) => ({
        label: `${c.crumb} i ${city.name}`,
        to: `/${c.slug}/${city.slug}`,
      })),
      ...CITIES.filter((c) => c.slug !== city.slug)
        .slice(0, 8)
        .map((c) => ({ label: `Lån i ${c.name}`, to: `/lan/${c.slug}` })),
    ];

    return (
      <PseoPage
        h1={`Lån i ${city.name} – jämför långivare och räntor`}
        intro={`${city.note} Här jämför du långivare som lånar ut till privatpersoner i ${city.name} och övriga ${city.county}. Alla ansökningar sköts digitalt med BankID, så du behöver aldrig besöka ett kontor.`}
        amount={amount}
        onAmountChange={setAmount}
        amountEditable
        lenders={lenders}
        tablesLabel={formatKr(amount)}
        crumbs={[{ label: "Hem", to: "/" }, { label: "Privatlån", to: "/privatlan" }, { label: `Lån i ${city.name}` }]}
        faq={cityFaq(city.name)}
        editorial={editorialFor(amount, { place: city.name })}
        relatedTitle={`Lån i ${city.name} och närliggande sidor`}
        related={related}
        epi="pseo-lan-stad"
        extraContent={
          <section className="mx-auto max-w-3xl px-4 pt-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl font-black text-foreground">Låna pengar i {city.name}</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
              {city.name} har cirka {formatPopulation(city.population)} invånare och ett genomsnittligt bostadspris runt{" "}
              {formatKr(city.homePrice)}. Boendekostnaden är den enskilt viktigaste posten när banken räknar på hur
              mycket du kan låna, eftersom den vägs mot din inkomst i den så kallade kvar-att-leva-på-kalkylen.
            </p>
            <h3 className="mt-6 font-display text-lg font-bold text-foreground">
              Samma villkor i {city.name} som i resten av landet
            </h3>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
              Ingen av långivarna sätter räntan efter postnummer. Vill du räkna på din egen situation innan du ansöker
              använder du vår{" "}
              <Link to="/guider/lanekalkyl" className="text-primary underline underline-offset-2">
                lånekalkylator
              </Link>
              .
            </p>
          </section>
        }
      />
    );
  }

  const kr = formatKr(data.amount);
  return (
    <PseoPage
      h1={`Jämför lån på ${kr} – hitta bäst ränta`}
      intro={`Ska du låna ${kr}? Nedan jämför du de långivare och låneförmedlare som faktiskt beviljar ${kr}. Justera återbetalningstiden i kalkylatorn för att se ungefärlig månadskostnad innan du ansöker.`}
      amount={amount}
      onAmountChange={setAmount}
      amountEditable
      lenders={lenders}
      crumbs={[{ label: "Hem", to: "/" }, { label: "Privatlån", to: "/privatlan" }, { label: kr }]}
      faq={tierFaq(data.amount)}
      editorial={editorialFor(data.amount)}
      epi="pseo-lan"
      relatedTitle="Andra belopp och kategorier"
      related={[
        ...MATRIX_CATEGORIES.filter((c) => amountsForCategory(c).includes(data.amount)).map((c) => ({
          label: `${c.crumb} ${kr}`,
          to: `/${c.slug}/${data.amount}-kr`,
        })),

        { label: "Alla privatlån", to: "/privatlan" },
        { label: "Lånekalkylator", to: "/guider/lanekalkyl" },
      ]}
    />
  );
}
